(() => {
  if (window.heritageCollectionCardGalleryLoaded) return;
  window.heritageCollectionCardGalleryLoaded = true;

  const ensureStylesheet = (gallery) => {
    const stylesheet = gallery.dataset.heritageCardGalleryStylesheet;
    if (!stylesheet) return;

    const stylesheetUrl = new URL(stylesheet, document.baseURI).href;
    const stylesheetExists = [...document.querySelectorAll('link[rel="stylesheet"]')]
      .some((link) => link.href === stylesheetUrl);
    if (stylesheetExists || [...document.styleSheets].some((sheet) => sheet.href === stylesheetUrl)) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = stylesheetUrl;
    link.dataset.heritageCardGalleryStylesheet = 'true';
    document.head.appendChild(link);
  };

  const shouldPlayIntro = (gallery) => {
    const surface = gallery.closest('.product-grid, #predictive-search-results-products-list');
    return !surface || surface.querySelector('[data-heritage-card-gallery]') === gallery;
  };

  const initGallery = (gallery) => {
    if (gallery.dataset.heritageGalleryReady === 'true') return;

    ensureStylesheet(gallery);

    const viewport = gallery.querySelector('[data-heritage-card-gallery-viewport]');
    const slides = [...gallery.querySelectorAll('[data-heritage-card-gallery-slide]')];
    const previous = gallery.querySelector('[data-heritage-card-gallery-previous]');
    const next = gallery.querySelector('[data-heritage-card-gallery-next]');

    if (!viewport || slides.length < 2) return;
    gallery.dataset.heritageGalleryReady = 'true';

    const currentIndex = () => Math.round(viewport.scrollLeft / viewport.clientWidth);
    const updateArrows = () => {
      const index = currentIndex();
      if (previous) previous.disabled = index <= 0;
      if (next) next.disabled = index >= slides.length - 1;
    };
    let introTimer = null;
    const stopIntro = () => {
      gallery.dataset.heritageGalleryIntroStopped = 'true';
      if (introTimer) window.clearTimeout(introTimer);
      viewport.style.scrollSnapType = '';
    };
    const showControls = () => gallery.classList.add('is-interacted');
    const moveTo = (index) => {
      stopIntro();
      showControls();
      viewport.scrollTo({ left: viewport.clientWidth * index, behavior: 'smooth' });
    };
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartIndex = 0;
    let suppressClick = false;

    previous?.addEventListener('click', () => moveTo(Math.max(0, currentIndex() - 1)));
    next?.addEventListener('click', () => moveTo(Math.min(slides.length - 1, currentIndex() + 1)));
    viewport.addEventListener('pointerdown', () => {
      stopIntro();
      showControls();
    }, { passive: true });
    viewport.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      if (!touch) return;

      stopIntro();
      showControls();
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartIndex = currentIndex();
    }, { passive: true });
    viewport.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;

      const distanceX = touch.clientX - touchStartX;
      const distanceY = touch.clientY - touchStartY;
      if (Math.abs(distanceX) < 30 || Math.abs(distanceX) <= Math.abs(distanceY)) return;

      suppressClick = true;
      const direction = distanceX < 0 ? 1 : -1;
      moveTo(Math.max(0, Math.min(slides.length - 1, touchStartIndex + direction)));
      window.setTimeout(() => { suppressClick = false; }, 500);
    }, { passive: true });
    viewport.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, true);
    viewport.addEventListener('scroll', () => {
      showControls();
      window.requestAnimationFrame(updateArrows);
    }, { passive: true });

    updateArrows();

    const playIntro = () => {
      if (gallery.dataset.heritageGalleryIntroPlayed === 'true') return;
      gallery.dataset.heritageGalleryIntroPlayed = 'true';

      const inlineScrollSnapType = viewport.style.scrollSnapType;
      const introSequence = [1, 0];
      let sequenceIndex = 0;
      viewport.style.scrollSnapType = 'none';

      const advance = () => {
        if (gallery.dataset.heritageGalleryIntroStopped === 'true') {
          viewport.style.scrollSnapType = inlineScrollSnapType;
          return;
        }

        viewport.scrollTo({ left: viewport.clientWidth * introSequence[sequenceIndex], behavior: 'smooth' });
        sequenceIndex += 1;

        if (sequenceIndex < introSequence.length) {
          introTimer = window.setTimeout(advance, 900);
        } else {
          introTimer = window.setTimeout(() => {
            viewport.style.scrollSnapType = inlineScrollSnapType;
          }, 500);
        }
      };

      introTimer = window.setTimeout(advance, 300);
    };

    if (!shouldPlayIntro(gallery)) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        playIntro();
      }, { threshold: 0.6 });
      observer.observe(gallery);
    } else {
      playIntro();
    }
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-heritage-card-gallery]').forEach(initGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initAll(node);
      });
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
